import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { TeamLeadService } from '../../services/teamLeadService';
import { Users, Share2, CheckCircle, Search, UserPlus, BarChart3, Calendar, MoreHorizontal, User, TrendingUp, Package, Settings, HelpCircle, AlertCircle } from 'lucide-react';

// Import new screens
import TeamLeadProfile from './Profile';
import TeamLeadSettings from './Settings';
import TeamLeadHelpSupport from './HelpSupport';
import TeamManagement from './TeamManagement';
import LeadAnalytics from './LeadAnalytics';
import AttendanceManagement from './AttendanceManagement';
import CourierBooking from './CourierBooking';
import DeliveredCustomers from './DeliveredCustomers';

const TeamLeadLeadDistribution = () => {
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectedTelecaller, setSelectedTelecaller] = useState('');
  
  const { data: teamMembersData } = useQuery({
    queryKey: ['teamLeadMembers', user?.id],
    queryFn: () => TeamLeadService.getTeamMembers(user?.id),
    enabled: !!user?.id,
  });

  const { data: availableLeadsData, refetch: refetchLeads, isLoading: isLoadingLeads } = useQuery({
    queryKey: ['teamLeadAvailableLeads', user?.id],
    queryFn: () => TeamLeadService.getAvailableLeads(user?.id),
    enabled: !!user?.id,
  });

  const teamMembers = teamMembersData?.data || [];
  const availableLeads = availableLeadsData?.data || [];

  const handleDistribute = async () => {
    if (!selectedTelecaller || selectedLeads.length === 0) {
      alert('Please select telecaller and leads');
      return;
    }
    const result = await TeamLeadService.distributeLeads(selectedLeads, [selectedTelecaller]);
    if (result.success) {
      alert('Leads distributed successfully!');
      setSelectedLeads([]);
      setSelectedTelecaller('');
      refetchLeads();
    } else {
      alert('Failed to distribute leads: ' + result.error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Lead Distribution</h1>
          <p className="text-gray-600 mt-1">Distribute leads to your team members</p>
        </div>
        <button
          onClick={handleDistribute}
          disabled={selectedLeads.length === 0 || !selectedTelecaller}
          className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50"
        >
          Distribute ({selectedLeads.length})
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Available Leads</h2>
          {isLoadingLeads ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
              <p className="text-gray-600">Loading leads...</p>
            </div>
          ) : availableLeads.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No available leads</p>
            </div>
          ) : (
            <div className="space-y-2">
              {availableLeads.map((lead) => (
                <div key={lead.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={selectedLeads.includes(lead.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedLeads([...selectedLeads, lead.id]);
                        } else {
                          setSelectedLeads(selectedLeads.filter(id => id !== lead.id));
                        }
                      }}
                      className="w-5 h-5"
                    />
                    <div>
                      <h3 className="font-semibold text-gray-900">{lead.name || 'Unknown'}</h3>
                      <p className="text-sm text-gray-600">{lead.contact_number || 'N/A'}</p>
                    </div>
                  </div>
                  <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-700">
                    {lead.status || 'pending'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Select Telecaller</h2>
          <select
            value={selectedTelecaller}
            onChange={(e) => setSelectedTelecaller(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4"
          >
            <option value="">Select telecaller</option>
            {teamMembers.map((member) => (
              <option key={member.id} value={member.id}>{member.name || member.email}</option>
            ))}
          </select>
          {selectedTelecaller && (
            <div className="mt-4 p-4 bg-indigo-50 rounded-lg">
              <p className="text-sm text-gray-600">Selected:</p>
              <p className="font-semibold text-gray-900">
                {teamMembers.find(m => m.id === selectedTelecaller)?.name || 'N/A'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TeamLeadLeadConfirmation = () => {
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState('TL_CONFIRMED');
  const [confirmNotes, setConfirmNotes] = useState('');

  const { data: pendingLeadsData, refetch, isLoading } = useQuery({
    queryKey: ['teamLeadPendingLeads', user?.id],
    queryFn: () => TeamLeadService.getLeadsForConfirmation(user?.id),
    enabled: !!user?.id,
  });
  const pendingLeads = pendingLeadsData?.data || [];

  const handleConfirm = async () => {
    if (selectedLeads.length === 0) {
      alert('Please select at least one lead');
      return;
    }

    setIsConfirming(true);
    try {
      const result = await TeamLeadService.confirmLeads(
        selectedLeads,
        user?.id,
        confirmStatus,
        confirmNotes
      );

      if (result.success) {
        alert('Leads confirmed successfully!');
        setSelectedLeads([]);
        setConfirmNotes('');
        refetch();
      } else {
        alert('Failed to confirm leads: ' + result.error);
      }
    } catch (error) {
      alert('Error confirming leads: ' + error.message);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Lead Confirmation</h1>
          <p className="text-gray-600 mt-1">Confirm leads for order processing</p>
        </div>
        <button
          onClick={handleConfirm}
          disabled={selectedLeads.length === 0 || isConfirming}
          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
        >
          {isConfirming ? 'Confirming...' : `Confirm Selected (${selectedLeads.length})`}
        </button>
      </div>

      {selectedLeads.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmation Details</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={confirmStatus}
                onChange={(e) => setConfirmStatus(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="TL_CONFIRMED">Confirmed</option>
                <option value="TL_NOT_ANSWER">Not Answer</option>
                <option value="TL_CALLBACK">Callback</option>
                <option value="TL_NOT_INTERESTED">Not Interested</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
              <textarea
                value={confirmNotes}
                onChange={(e) => setConfirmNotes(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="Add confirmation notes..."
              />
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading leads...</p>
        </div>
      ) : pendingLeads.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <CheckCircle size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No pending leads for confirmation</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6">
            <div className="space-y-3">
              {pendingLeads.map((lead) => (
                <div key={lead.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={selectedLeads.includes(lead.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedLeads([...selectedLeads, lead.id]);
                        } else {
                          setSelectedLeads(selectedLeads.filter(id => id !== lead.id));
                        }
                      }}
                      className="w-5 h-5"
                    />
                    <div>
                      <h3 className="font-semibold text-gray-900">{lead.name || 'Unknown'}</h3>
                      <p className="text-sm text-gray-600">{lead.contact_number || 'N/A'}</p>
                      {lead.address && <p className="text-xs text-gray-500 mt-1">{lead.address}</p>}
                    </div>
                  </div>
                  <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-700">
                    {lead.status || 'Pending Confirmation'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TeamLeadPerformance = () => {
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  
  const { data: teamStatsData } = useQuery({
    queryKey: ['teamLeadStats', user?.id],
    queryFn: () => TeamLeadService.getTeamStats(user?.id),
    enabled: !!user?.id,
  });
  const teamStats = teamStatsData?.data || {};

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Team Performance</h1>
        <p className="text-gray-600 mt-1">View your team's performance metrics</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Total Leads</p>
            <BarChart3 size={20} className="text-indigo-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{teamStats.totalLeads || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Confirmed</p>
            <CheckCircle size={20} className="text-green-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{teamStats.confirmed || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Booked</p>
            <Share2 size={20} className="text-blue-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{teamStats.booked || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Conversion Rate</p>
            <BarChart3 size={20} className="text-purple-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{teamStats.conversionRate || 0}%</p>
        </div>
      </div>
    </div>
  );
};

const TeamLeadBooked = () => {
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [hasAwb, setHasAwb] = useState(false);
  const [onlyNdr, setOnlyNdr] = useState(false);
  
  const { data: bookedOrdersData, isLoading } = useQuery({
    queryKey: ['teamLeadBookedOrders', user?.id],
    queryFn: () => TeamLeadService.getBookedOrders(user?.id),
    enabled: !!user?.id,
  });
  const bookedOrders = bookedOrdersData?.data || [];

  // Filter orders
  const filteredOrders = bookedOrders.filter((order) => {
    // Search filter
    const matchesSearch = !searchText ||
      (order.customer_name?.toLowerCase().includes(searchText.toLowerCase())) ||
      (order.contact_number?.includes(searchText)) ||
      (order.order_id?.toString().includes(searchText)) ||
      (order.id?.toString().includes(searchText));

    // Status filter
    const orderStatus = (order.current_status_type || order.status || '').toLowerCase();
    const matchesStatus = !statusFilter || 
      orderStatus === statusFilter.toLowerCase() ||
      (statusFilter === 'ndr' && orderStatus.includes('ndr')) ||
      (statusFilter === 'delivered' && (orderStatus === 'delivered' || orderStatus === 'DELIVERED'));

    // Date filter
    const orderDate = order.order_created_at || order.created_at;
    const matchesDate = !orderDate || (
      orderDate >= startDate &&
      orderDate <= endDate
    );

    // AWB filter
    const matchesAwb = !hasAwb || !!(order.awb_number || order.tracking_number);

    // NDR filter
    const matchesNdr = !onlyNdr || orderStatus.includes('ndr') || orderStatus.includes('NDR');

    return matchesSearch && matchesStatus && matchesDate && matchesAwb && matchesNdr;
  });

  const statusOptions = [
    { value: '', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'ready_to_ship', label: 'Ready to Ship' },
    { value: 'shipped', label: 'Shipped' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'out_for_delivery', label: 'Out for Delivery' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'ndr', label: 'NDR' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'rto', label: 'RTO' },
  ];

  const getStatusBadgeColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('delivered')) return 'bg-green-100 text-green-700';
    if (s.includes('pending') || s.includes('ready')) return 'bg-yellow-100 text-yellow-700';
    if (s.includes('shipped') || s.includes('transit') || s.includes('delivery')) return 'bg-blue-100 text-blue-700';
    if (s.includes('ndr')) return 'bg-orange-100 text-orange-700';
    if (s.includes('cancelled') || s.includes('rto')) return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Booked Orders</h1>
        <p className="text-gray-600 mt-1">View all booked orders from your team</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-4">
        {/* Search */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search by customer name, phone, or order ID..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>

        {/* Date Range */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={endDate}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Status Filter Chips */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <div className="flex flex-wrap gap-2">
            {statusOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setStatusFilter(option.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === option.value
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Toggle Filters */}
        <div className="flex gap-4">
          <button
            onClick={() => setHasAwb(!hasAwb)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              hasAwb
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Package size={16} />
            <span>With AWB</span>
          </button>
          <button
            onClick={() => setOnlyNdr(!onlyNdr)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              onlyNdr
                ? 'bg-orange-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <AlertCircle size={16} />
            <span>NDR Only</span>
          </button>
        </div>

        {/* Results Count */}
        <div className="text-sm text-gray-600">
          Showing {filteredOrders.length} of {bookedOrders.length} orders
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading orders...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <CheckCircle size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No orders found matching your filters</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telecaller</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">AWB</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredOrders.map((order) => (
                <tr key={order.order_id || order.id} className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/order/${order.order_id || order.id}`)}
                >
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                    #{order.order_id || order.id?.slice(0, 8)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{order.customer_name || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{order.telecaller_name || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    ₹{Number(order.final_amount || order.amount || 0).toLocaleString('en-IN')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    {order.order_created_at || order.created_at 
                      ? new Date(order.order_created_at || order.created_at).toLocaleDateString() 
                      : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadgeColor(order.current_status_type || order.status)}`}>
                      {(order.current_status_type || order.status || 'Booked').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    {order.awb_number || order.tracking_number ? (
                      <span className="text-xs text-indigo-600 font-medium">
                        {order.awb_number || order.tracking_number}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const TeamLeadMore = () => {
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const navigate = useNavigate();

  const menuItems = [
    {
      title: 'Profile',
      subtitle: 'Manage your profile and team info',
      icon: User,
      color: 'bg-purple-100 text-purple-700',
      onClick: () => navigate('/teamlead/profile'),
    },
    {
      title: 'Performance Reports',
      subtitle: 'View team and individual performance',
      icon: BarChart3,
      color: 'bg-blue-100 text-blue-700',
      onClick: () => navigate('/teamlead/performance'),
    },
    {
      title: 'Team Management',
      subtitle: 'Manage telecaller assignments',
      icon: Users,
      color: 'bg-green-100 text-green-700',
      onClick: () => navigate('/teamlead/team-management'),
    },
    {
      title: 'Lead Analytics',
      subtitle: 'Detailed lead conversion analytics',
      icon: TrendingUp,
      color: 'bg-purple-100 text-purple-700',
      onClick: () => navigate('/teamlead/lead-analytics'),
    },
    {
      title: 'Attendance Management',
      subtitle: 'Mark and review daily attendance',
      icon: Calendar,
      color: 'bg-cyan-100 text-cyan-700',
      onClick: () => navigate('/teamlead/attendance'),
    },
    {
      title: 'Courier Booking',
      subtitle: 'Book courier services for orders',
      icon: Package,
      color: 'bg-emerald-100 text-emerald-700',
      onClick: () => navigate('/teamlead/courier-booking'),
    },
    {
      title: 'Delivered Customers',
      subtitle: 'View delivered customer information',
      icon: CheckCircle,
      color: 'bg-teal-100 text-teal-700',
      onClick: () => navigate('/teamlead/delivered-customers'),
    },
    {
      title: 'Settings',
      subtitle: 'App preferences and configuration',
      icon: Settings,
      color: 'bg-gray-100 text-gray-700',
      onClick: () => navigate('/teamlead/settings'),
    },
    {
      title: 'Help & Support',
      subtitle: 'Get help and contact support',
      icon: HelpCircle,
      color: 'bg-yellow-100 text-yellow-700',
      onClick: () => navigate('/teamlead/help'),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">More Options</h1>
        <p className="text-gray-600 mt-1">Additional features and settings</p>
      </div>

      {/* Profile Section */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
            <span className="text-white font-bold text-2xl">
              {user?.name?.charAt(0).toUpperCase() || 'T'}
            </span>
          </div>
          <div>
            <h2 className="text-2xl font-bold">{user?.name || 'Team Lead'}</h2>
            <p className="text-white/80">{user?.email || 'No email'}</p>
            <p className="text-sm text-white/70">Team Lead</p>
          </div>
        </div>
      </div>

      {/* Menu Items */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="space-y-2">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={index}
                onClick={item.onClick}
                className="w-full flex items-center justify-between p-4 rounded-lg hover:bg-gray-50 transition"
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${item.color}`}>
                    <Icon size={20} />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900">{item.title}</h3>
                    <p className="text-sm text-gray-500">{item.subtitle}</p>
                  </div>
                </div>
                <MoreHorizontal size={20} className="text-gray-400" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export { TeamLeadLeadDistribution, TeamLeadLeadConfirmation, TeamLeadPerformance, TeamLeadBooked, TeamLeadMore };